// Permissions screen: read-only catalogue of every permission key, grouped into
// a two-level Area → Domain tree (mirrors solya-app's PermissionsCatalog). Area
// and sub-domain are derived from the key, so the POS keys fan out into their
// real sub-domains instead of collapsing into one "pos" section. A sticky filter
// bar (area chips + text search) narrows what renders so users don't scroll a
// long flat list; accordions stay collapsed by default and auto-expand while a
// search term is active.

import { useMemo, useState } from "react"
import { adminApi } from "../api/endpoints.js"
import type { PermissionDto } from "../api/types.js"
import { PermissionFilterBar, usePermissionFilter } from "../components/PermissionFilterBar.js"
import { fr } from "../i18n/fr.js"
import { areaLabel, domainLabel, groupByAreaAndDomain } from "../lib/grouping.js"
import { useAsync } from "../lib/useAsync.js"
import { Alert, Chip, Spinner } from "../ui/primitives.js"

export function PermissionsPage() {
  const perms = useAsync<PermissionDto[]>(() => adminApi.listPermissions(), [])
  const areas = useMemo(() => groupByAreaAndDomain(perms.data ?? []), [perms.data])
  const filter = usePermissionFilter(areas)
  const hasCatalogue = areas.length > 0

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{fr.permissions.title}</h1>
          <p>{fr.permissions.description}</p>
        </div>
      </div>

      {perms.loading ? <Spinner /> : null}
      {perms.error ? <Alert variant="error">{fr.permissions.error}</Alert> : null}
      {perms.data && !perms.loading && !hasCatalogue ? <Alert>{fr.permissions.empty}</Alert> : null}

      {hasCatalogue ? (
        <PermissionFilterBar
          areaKeys={filter.areaKeys}
          area={filter.area}
          onAreaChange={filter.setArea}
          query={filter.query}
          onQueryChange={filter.setQuery}
        />
      ) : null}

      {hasCatalogue && filter.filtered.length === 0 ? (
        <Alert>{fr.permissions.filter.empty}</Alert>
      ) : null}

      {filter.filtered.map((area) => (
        <section key={area.area} className="area-section">
          <h2 className="area-section__title">{areaLabel(area.area, fr.permissions.area)}</h2>
          {area.domains.map((group) => (
            <DomainAccordion
              key={group.domain}
              domain={group.domain}
              permissions={group.permissions}
              forceOpen={filter.searchActive}
            />
          ))}
        </section>
      ))}
    </div>
  )
}

function DomainAccordion({
  domain,
  permissions,
  forceOpen = false
}: { domain: string; permissions: PermissionDto[]; forceOpen?: boolean }) {
  const [open, setOpen] = useState(false)
  // While a search is active every matching domain auto-expands so hits are
  // visible without manual expand; otherwise the accordion is user-controlled.
  const isOpen = forceOpen || open
  return (
    <div className="accordion">
      <button
        type="button"
        className="accordion__summary"
        aria-expanded={isOpen}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{domainLabel(domain, fr.permissions.domain)}</span>
        <Chip>{fr.permissions.count(permissions.length)}</Chip>
      </button>
      {isOpen ? (
        <div className="accordion__body">
          {permissions.map((p) => (
            <Chip key={p.id} mono title={p.description ?? undefined}>
              {p.key}
            </Chip>
          ))}
        </div>
      ) : null}
    </div>
  )
}
